/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { FC, useCallback } from 'react';
import { Spotlight } from '@ldp-web/component-library/ui/organisms/spotlight';
import { useSDMCartContext, useSDMRouterContext } from '@ldp/sdm-contexts';
import { useFeatureFlags, useSettingsContext } from '@ldp/common-contexts';
import {
  SDMAnalyticEvents,
  SDMSnowplowAdEvents,
  SDMSnowplowProductEvents,
  TCTAContextAnalytics,
  TSDMDTMProductAnalyticsContext,
  getSDMSnowplowAddToCartContext,
  getSnowplowAdTrackingProductClickContext,
  getSnowplowProductContext,
  isValidProductPosition,
  useAddToCartTrackEvent,
} from '@ldp/sdm-analytics-utils';
import { IBrandSpotlightComponentProps } from './brand-spotlight.interface';
import { IComponentCommonProps, IComponentIdProps, SDMLanguageType } from '@ldp/common-types';
import { useRedirectLink } from '../../utils';
import { paramCase, useTrackMediaAnalyticsAdClick } from '@ldp/media-analytics';
import { Badge } from '@ldp-web/component-library/types';
import { useGetA11yCartMessages } from '../../hooks';
import { useSimpleTranslation } from '@ldp/simple-i18n';
import { WrapperComponent } from '../wrapper';

export type TBrandSpotlightComponentProps = IBrandSpotlightComponentProps & IComponentCommonProps & IComponentIdProps;

// SSR checks
const IS_SSR = typeof window === 'undefined';

/** Component for Brand spotlight component */
export const BrandSpotlightComponent: FC<TBrandSpotlightComponentProps> = (props) => {
  const {
    headline,
    isSponsored,
    productTiles,
    callToAction,
    callToAction2,
    analytics,
    badge,
    styling,
    contentId,
    id,
    ...rest
  } = props;
  const settings = useSettingsContext();
  const i18n = useSimpleTranslation();
  const { getFeatureFlags } = useFeatureFlags();
  const { EnableHeliosSnowplow } = getFeatureFlags();
  const { addToCart, entries, isLoading: isCartLoading } = useSDMCartContext();
  const trackAdClick = useTrackMediaAnalyticsAdClick(true);
  const { router } = useSDMRouterContext();
  const redirectToPage = useRedirectLink(router);
  const trackFn = !IS_SSR ? useAddToCartTrackEvent(EnableHeliosSnowplow) : () => {};
  const a11yCartMessages = useGetA11yCartMessages();
  const LINE_OF_BUSINESS_SDM = 'sdm';

  const genericAnalytics = analytics?.genericAnalytics;
  const genericAnalyticsData = genericAnalytics ? { genericAnalytics: genericAnalytics } : {};

  // Create a wrapper over the tracking function to pass productTiles
  const trackFnWrapper = useCallback(
    (type: string, context: unknown): void => {
      const { callToActionElement } = context as TCTAContextAnalytics;
      const compData = {
        id: props.contentId || null,
        type: props.componentId || null,
        name: headline,
      };
      const analyticComponentContext = {
        engine: 'helios',
        number_of_children: productTiles.length,
        ...compData,
      };
      if (type === SDMAnalyticEvents.UI_ENGAGEMENT) {
        //keeping existing ui_engagement event which is not handled at sdm-frontshop
        trackFn(type, {
          ...(context as Record<string, unknown>),
          ...genericAnalyticsData,
        });
        const uiEngagementComponentContext = {
          click_element: callToActionElement?.text,
          click_action: 'cta-click',
          cta_url: callToActionElement?.url,
          ...analyticComponentContext,
        };
        // new event trigger for sdm-frontshop
        trackFn('heliosBrandSpotlightCTA', {
          component: uiEngagementComponentContext,
          dsModel: genericAnalytics?.ds_model ?? analytics?.dsModel ?? [],
        });
        return;
      }
      const contextData = getSDMSnowplowAddToCartContext(
        context as TSDMDTMProductAnalyticsContext,
        productTiles,
        compData
      );
      if (contextData) {
        if (isSponsored) {
          trackFn(type, {
            ...getSnowplowAdTrackingProductClickContext(SDMSnowplowAdEvents.CLICK, EnableHeliosSnowplow, {
              ad: {
                ...analytics?.mediaAnalytics,
              },
              product: {
                ...contextData.productData,
                productPosition: contextData.productPosition,
                isSponsored,
              },
            }),
            ...contextData,
            ...genericAnalyticsData,
            dsModel: genericAnalytics?.ds_model ?? analytics?.dsModel ?? [],
          });
          if (type === 'ad_click') {
            trackAdClick(
              props as unknown as Record<string, unknown>,
              {
                product: contextData.productData,
                productPosition: contextData.productPosition,
              },
              {
                creative_id: paramCase(contextData.productData.title),
              }
            );
          }
          if (type === SDMSnowplowProductEvents.TILE_CLICK) {
            const {
              data: { productId },
            } = context as TSDMDTMProductAnalyticsContext;
            const productPosition = productTiles.findIndex((product) => product.productId === productId);
            const productContextData = productTiles[productPosition];
            const contextData = getSnowplowProductContext(
              productContextData,
              isValidProductPosition(productContextData?.index) ? productContextData?.index - 1 : productPosition + 1
            );
            const eventContextData = {
              event: type,
              component: analyticComponentContext,
              heliosAnalytics: true,
              heliosSnowplow: EnableHeliosSnowplow,
              productContext: {
                ...contextData,
              },
              productPosition: contextData?.productPosition,
              ...genericAnalyticsData,
              dsModel: genericAnalytics?.ds_model ?? analytics?.dsModel ?? [],
            };
            trackFn('heliosBrandSpotlightProductClick', eventContextData);
          }
        } else {
          trackFn(type, {
            ...contextData,
            ...(genericAnalytics?.dsModel ? { dsModel: genericAnalytics?.dsModel } : {}),
          });
        }
      } else {
        trackAdClick(props as unknown as Record<string, unknown>, undefined, {
          creative_id: paramCase(callToAction?.text || ''),
        });
      }
    },
    [
      productTiles,
      trackFn,
      EnableHeliosSnowplow,
      analytics?.mediaAnalytics,
      callToAction?.text,
      headline,
      isSponsored,
      props.componentId,
      props.contentId,
      trackAdClick,
    ]
  );

  return (
    <WrapperComponent isExtraLargeSpacing styling={styling} id={id}>
      <Spotlight
        {...rest}
        productTileData={productTiles as any}
        language={settings.locale as SDMLanguageType}
        entries={entries}
        badge={badge as Badge}
        addToCartCB={addToCart}
        isCartLoading={isCartLoading}
        i18nskipText={i18n('skip')}
        headline={headline}
        tileAction={redirectToPage}
        isSponsored={isSponsored}
        trackingFn={trackFnWrapper}
        cta={callToAction}
        callToAction={callToAction2}
        lob={LINE_OF_BUSINESS_SDM}
        isMedia={true}
        a11yMessages={a11yCartMessages}
        data-testid={contentId}
        sponsoredInfoBadge={i18n('sponsoredLabel')}
      />
    </WrapperComponent>
  );
};
